import {Box} from '@material-ui/core';
import {Button} from '@wandb/weave/components/Button';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {PaginationControls} from '../../../common/PaginationControls';
import {buildCompositeMetricsMap} from '../../compositeMetricsUtil';
import {
  BOX_RADIUS,
  PLOT_HEIGHT,
  PLOT_PADDING,
  STANDARD_BORDER,
  STANDARD_PADDING,
} from '../../ecpConstants';
import {getOrderedCallIds} from '../../ecpState';
import {EvaluationComparisonState} from '../../ecpState';
import {
  dimensionUnit,
  flattenedDimensionPath,
  resolveSummaryMetricValueForEvaluateCall,
} from '../../ecpUtil';
import {HorizontalBox, VerticalBox} from '../../Layout';
import {MetricsSelector} from './MetricsSelector';
import type {BarChartData} from './RechartsBarChart';
import {RechartsBarChart} from './RechartsBarChart';
import type {RadarPlotData, Series} from './RechartsRadarChart';
import {RechartsRadarChart} from './RechartsRadarChart';

/**
 * Summary plots produce plots to summarize evaluation comparisons.
 */
export const SummaryPlots: React.FC<{
  state: EvaluationComparisonState;
  setSelectedMetrics: (newModel: Record<string, boolean>) => void;
}> = ({state, setSelectedMetrics}) => {
  const {radarData, allMetricNames} = usePlotDataFromMetrics(state);
  const {selectedMetrics} = state;

  // Initialize selectedMetrics if null
  useEffect(() => {
    if (selectedMetrics == null) {
      setSelectedMetrics(
        Object.fromEntries(Array.from(allMetricNames).map(m => [m, true]))
      );
    }
  }, [selectedMetrics, setSelectedMetrics, allMetricNames]);

  const filteredData = useFilteredData(radarData, selectedMetrics);
  const normalizedRadarData = normalizeDataForRadarPlot(filteredData);
  const barPlotData = useBarPlotData(filteredData);

  const {
    containerRef,
    containerWidth,
    plotsPerPage,
    currentPage,
    setCurrentPage,
  } = useContainerDimensions();

  const handleCloseMetric = React.useCallback(
    (metric: string) => {
      if (selectedMetrics) {
        setSelectedMetrics({
          ...selectedMetrics,
          [metric]: false,
        });
      }
    },
    [setSelectedMetrics, selectedMetrics]
  );

  const {plotsToShow, totalPlots, startIndex, endIndex, totalPages} =
    usePaginatedPlots(
      barPlotData,
      Math.max(1, plotsPerPage - 2),
      currentPage,
      handleCloseMetric
    );

  return (
    <VerticalBox
      sx={{
        paddingLeft: STANDARD_PADDING,
        paddingRight: STANDARD_PADDING,
        // flex: '1 1 auto',
        width: '100%',
        gridGap: STANDARD_PADDING / 2,
        visibility: containerWidth === 0 ? 'hidden' : 'visible',
      }}>
      <HorizontalBox
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <div style={{display: 'flex', alignItems: 'center'}}>
          <MetricsSelector
            selectedMetrics={selectedMetrics}
            setSelectedMetrics={setSelectedMetrics}
            allMetrics={Array.from(allMetricNames)}
          />
        </div>
        <div style={{display: 'flex', alignItems: 'center'}}>
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={endIndex}
            totalItems={totalPlots}
            onPrevPage={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
            onNextPage={() =>
              setCurrentPage(prev => Math.min(prev + 1, totalPages - 1))
            }
          />
        </div>
      </HorizontalBox>

      <div ref={containerRef} style={{width: '100%'}}>
        <HorizontalBox>
          <RechartsRadarChart data={normalizedRadarData} withFullscreen />
          {plotsToShow}
        </HorizontalBox>
      </div>
    </VerticalBox>
  );
};

const BarPlotBox: React.FC<{
  plot: {barData: BarChartData; metric: string};
  onClose: (metric: string) => void;
}> = ({plot, onClose}) => (
  <Box
    sx={{
      position: 'relative',
      height: PLOT_HEIGHT,
      width: PLOT_HEIGHT,
      borderRadius: BOX_RADIUS,
      border: STANDARD_BORDER,
      paddingTop: 20,
      paddingBottom: PLOT_PADDING,
      paddingLeft: PLOT_PADDING,
      paddingRight: PLOT_PADDING,
      flex: '1 1 auto',
    }}>
    <div
      style={{
        position: 'absolute',
        top: 8,
        left: 32,
        right: 32,
        textAlign: 'center',
        fontSize: '12px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
      {plot.metric}
    </div>
    <Button
      variant="ghost"
      size="small"
      style={{
        position: 'absolute',
        top: 4,
        right: 4,
        minWidth: 0,
        width: 24,
        height: 24,
        padding: 0,
        zIndex: 1,
      }}
      onClick={() => onClose(plot.metric)}
      icon="close"
      aria-label={`Hide ${plot.metric}`}
    />
    <RechartsBarChart data={plot.barData} />
  </Box>
);

const useFilteredData = (
  radarData: RadarPlotData,
  selectedMetrics: Record<string, boolean> | undefined
) =>
  useMemo(() => {
    if (!radarData) {
      return {};
    }
    const data: RadarPlotData = {};

    Object.entries(radarData).forEach(([callId, callData]) => {
      if (callId === 'metricMetadata') return;

      const series = callData as Series;
      const metrics: {[metric: string]: number} = {};
      for (const [metric, value] of Object.entries(series.metrics)) {
        if (selectedMetrics?.[metric]) {
          metrics[metric] = value;
        }
      }
      if (Object.keys(metrics).length > 0) {
        data[callId] = {metrics, name: series.name, color: series.color};
      }
    });

    if (radarData.metricMetadata) {
      data.metricMetadata = radarData.metricMetadata;
    }
    return data;
  }, [radarData, selectedMetrics]);

function getMetricValuesMap(radarData: RadarPlotData): {
  [metric: string]: number[];
} {
  const metricValues: {[metric: string]: number[]} = {};
  Object.entries(radarData).forEach(([callId, callData]) => {
    if (callId === 'metricMetadata') return;

    const series = callData as Series;
    Object.entries(series.metrics).forEach(([metric, value]) => {
      if (!metricValues[metric]) {
        metricValues[metric] = [];
      }
      metricValues[metric].push(value);
    });
  });
  return metricValues;
}

function normalizeMetricValues(values: number[]): {
  normalizedValues: number[];
  normalizer: number;
} {
  const min = Math.min(...values);
  const max = Math.max(...values);

  if (min === max) {
    return {
      normalizedValues: values.map(() => 0.5),
      normalizer: 1,
    };
  }

  // Handle negative values by shifting
  const shiftedValues = min < 0 ? values.map(v => v - min) : values;
  const maxValue = min < 0 ? max - min : max;

  const maxPower = Math.ceil(Math.log2(maxValue));
  const normalizer = Math.pow(2, maxPower);

  return {
    normalizedValues: shiftedValues.map(v => v / normalizer),
    normalizer,
  };
}

function normalizeDataForRadarPlot(
  radarDataOriginal: RadarPlotData
): RadarPlotData {
  // Deep copy series data (excluding metadata), preserving original values
  const radarData: RadarPlotData = {} as RadarPlotData;
  Object.entries(radarDataOriginal).forEach(([callId, callData]) => {
    if (callId === 'metricMetadata') return;

    const series = callData as Series;
    radarData[callId] = {
      ...series,
      metrics: {...series.metrics},
      originalMetrics: {...series.metrics}, // Store original values for tooltips
    };
  });

  const metricValues = getMetricValuesMap(radarData);

  // Normalize each metric independently (only update metrics, not originalMetrics)
  // Skip normalization for binary metrics - they're already in [0,1]
  Object.entries(metricValues).forEach(([metric, values]) => {
    const isBinary =
      radarDataOriginal.metricMetadata?.[metric]?.scoreType === 'binary';

    if (isBinary) {
      // Binary metrics are already in [0,1], no normalization needed
      return;
    }

    const {normalizedValues} = normalizeMetricValues(values);
    let index = 0;
    Object.entries(radarData).forEach(([callId, callData]) => {
      if (callId === 'metricMetadata') return;
      const series = callData as Series;
      series.metrics[metric] = normalizedValues[index++];
    });
  });

  if (radarDataOriginal.metricMetadata) {
    radarData.metricMetadata = radarDataOriginal.metricMetadata;
  }

  return radarData;
}

const useBarPlotData = (filteredData: RadarPlotData) =>
  useMemo(() => {
    const metrics: {
      [metric: string]: {
        callIds: string[];
        values: number[];
        name: string;
        colors: string[];
        evalNames: string[];
      };
    } = {};

    // Reorganize data by metric instead of by call
    Object.entries(filteredData).forEach(([callId, callData]) => {
      if (callId === 'metricMetadata') return;

      const series = callData as Series;
      for (const [metric, value] of Object.entries(series.metrics) as Array<
        [string, number]
      >) {
        if (!metrics[metric]) {
          metrics[metric] = {
            callIds: [],
            values: [],
            name: metric,
            colors: [],
            evalNames: [],
          };
        }
        metrics[metric].callIds.push(callId);
        metrics[metric].values.push(value);
        metrics[metric].colors.push(series.color);
        metrics[metric].evalNames.push(series.name);
      }
    });

    // Convert metrics object to bar chart data format
    const metricMetadata = (filteredData as RadarPlotData).metricMetadata;
    return Object.entries(metrics).map(([metric, metricBin]) => {
      const maxY = Math.max(...metricBin.values) * 1.1;
      const minY = Math.min(...metricBin.values, 0);
      const barData: BarChartData = {
        x: metricBin.callIds,
        y: metricBin.values,
        colors: metricBin.colors,
        names: metricBin.evalNames,
        name: metric,
        yRange: [minY, maxY],
        metadata: metricMetadata?.[metric],
      };
      return {barData, metric};
    });
  }, [filteredData]);

const useContainerDimensions = () => {
  const [containerWidth, setContainerWidth] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  // Use a callback ref to handle the element change correctly
  const observerRef = useRef<ResizeObserver | null>(null);

  const containerRef = React.useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (node !== null) {
      setContainerWidth(node.offsetWidth);

      const observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          setContainerWidth((entry.target as HTMLElement).offsetWidth);
        }
      });
      observer.observe(node);
      observerRef.current = observer;
    }
  }, []);

  const plotsPerPage = useMemo(() => {
    return Math.max(1, Math.floor(containerWidth / PLOT_HEIGHT));
  }, [containerWidth]);

  return {
    containerRef,
    containerWidth,
    plotsPerPage,
    currentPage,
    setCurrentPage,
  };
};

const usePaginatedPlots = (
  barPlotData: Array<{barData: BarChartData; metric: string}>,
  plotsPerPage: number,
  currentPage: number,
  onClose: (metric: string) => void
) => {
  const totalPages = Math.ceil(barPlotData.length / plotsPerPage);

  const plotsToShow = useMemo(() => {
    const startIdx = currentPage * plotsPerPage;
    const endIdx = startIdx + plotsPerPage;
    return barPlotData
      .slice(startIdx, endIdx)
      .map((plot, index) => (
        <BarPlotBox
          key={`bar-${startIdx + index}`}
          plot={plot}
          onClose={onClose}
        />
      ));
  }, [currentPage, plotsPerPage, barPlotData, onClose]);

  // Calculate pagination details
  const totalPlots = barPlotData.length;
  const startIndex = totalPlots === 0 ? 0 : currentPage * plotsPerPage + 1;
  const endIndex = Math.min(startIndex + plotsToShow.length - 1, totalPlots);

  return {plotsToShow, totalPlots, startIndex, endIndex, totalPages};
};

const usePlotDataFromMetrics = (
  state: EvaluationComparisonState
): {radarData: RadarPlotData; allMetricNames: Set<string>} => {
  const compositeMetrics = useMemo(() => {
    return buildCompositeMetricsMap(state.summary, 'summary');
  }, [state]);
  const callIds = useMemo(() => {
    return getOrderedCallIds(state);
  }, [state]);

  return useMemo(() => {
    const metrics = Object.values(compositeMetrics)
      .map(scoreGroup => Object.values(scoreGroup.metrics))
      .flat()
      .map(metric => {
        const values = callIds.map(callId => {
          const metricDimension = Object.values(metric.scorerRefs).find(
            scorerRefData => scorerRefData.evalCallIds.includes(callId)
          )?.metric;
          if (!metricDimension) {
            return undefined;
          }
          const val = resolveSummaryMetricValueForEvaluateCall(
            metricDimension,
            state.summary.evaluationCalls[callId]
          );
          if (typeof val === 'boolean') {
            return val ? 1 : 0;
          } else {
            return val;
          }
        });
        const evalScores: {[evalCallId: string]: number | undefined} =
          Object.fromEntries(callIds.map((key, i) => [key, values[i]]));

        const metricDimension = Object.values(metric.scorerRefs)[0].metric;
        const metricLabel = flattenedDimensionPath(metricDimension);
        return {
          metricLabel,
          evalScores,
          metricDimension,
        };
      });

    const metricMetadata = Object.fromEntries(
      metrics.map(metric => [
        metric.metricLabel,
        {
          scoreType: metric.metricDimension.scoreType,
          unit: dimensionUnit(metric.metricDimension, true),
        },
      ])
    );

    const radarData: RadarPlotData = Object.fromEntries(
      callIds.map(callId => {
        const evalCall = state.summary.evaluationCalls[callId];
        return [
          evalCall.callId,
          {
            name: evalCall.name,
            color: evalCall.color,
            metrics: Object.fromEntries(
              metrics.map(metric => {
                return [
                  metric.metricLabel,
                  metric.evalScores[evalCall.callId] ?? 0,
                ];
              })
            ),
          },
        ];
      })
    ) as RadarPlotData;
    radarData.metricMetadata = metricMetadata;

    const allMetricNames = new Set(metrics.map(m => m.metricLabel));
    return {radarData, allMetricNames};
  }, [callIds, compositeMetrics, state.summary.evaluationCalls]);
};
